/**
 * Route-level permission check functions for use in TanStack Router `beforeLoad`.
 *
 * These are plain async functions (not React hooks) that can be called outside of React component trees. They use
 * `QueryClient.ensureQueryData` to fetch the organization and current user data, then run Cerbos ABAC checks locally.
 */ import { getAccessToken } from '@ui/token-storage';
import { checkDevopsFeature, getCheckDevopsFeatureQueryKey, getCurrentUser, getGetCurrentUserQueryKey, getGetOrganizationQueryKey, getOrganization } from 'ze-sdk';
import { getCanManageAppConfig } from './application/index';
import { getCanManageIntegrations, getCanManageOrgConfig } from './org/index';
import { getCanCreateProjectTeams, getCanManageContributorConfig, getCanManageProjConfig } from './project/index';
import { getCanManageSubscription, getCanViewSubscription } from './subscription/index';
// ---------------------------------------------------------------------------
// Data fetching helpers (same queries the hooks use, but for non-React contexts)
// ---------------------------------------------------------------------------
async function ensureOrganization(queryClient, orgName) {
    const result = await queryClient.ensureQueryData({
        queryKey: getGetOrganizationQueryKey({
            organization: orgName
        }),
        queryFn: async ()=>{
            return await getOrganization({
                organization: orgName
            });
        }
    });
    return result.value;
}
async function ensureCurrentUser(queryClient) {
    const token = getAccessToken();
    if (!token) return undefined;
    const user = await queryClient.ensureQueryData({
        queryKey: [
            'user',
            'current',
            ...getGetCurrentUserQueryKey()
        ],
        queryFn: async ()=>{
            const { value } = await getCurrentUser();
            return value !== null && value !== void 0 ? value : undefined;
        }
    });
    return user;
}
async function ensureDevopsFeature(queryClient) {
    const result = await queryClient.ensureQueryData({
        queryKey: getCheckDevopsFeatureQueryKey(),
        queryFn: ()=>checkDevopsFeature()
    });
    return Boolean(result);
}
// ---------------------------------------------------------------------------
// Common helper: resolve org + user for a given org name
// ---------------------------------------------------------------------------
async function resolveOrgAndUser(queryClient, orgName) {
    const [org, user] = await Promise.all([
        ensureOrganization(queryClient, orgName),
        ensureCurrentUser(queryClient)
    ]);
    if (!org || !user) return null;
    return {
        org,
        user
    };
}
// ---------------------------------------------------------------------------
// Route-level check functions
// ---------------------------------------------------------------------------
/** Check org management permissions (settings, delete, create project, etc.) */ export async function checkOrgPermissions(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    return getCanManageOrgConfig(resolved.org, resolved.user);
}
/** Check if user can manage deployment integrations at org level */ export async function checkDeploymentIntegrationsPermission(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return false;
    return getCanManageIntegrations(resolved.org, resolved.user);
}
/** Check if user can manage subscription */ export async function checkManageSubscriptionPermission(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return false;
    return getCanManageSubscription(resolved.org, resolved.user);
}
/** Check if user can view subscription */ export async function checkViewSubscriptionPermission(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return false;
    return getCanViewSubscription(resolved.org, resolved.user);
}
/** Check project contributor management permissions */ export async function checkContributorPermission(queryClient, orgName, projectName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    return getCanManageContributorConfig(resolved.org, resolved.user, {
        id: projectName
    });
}
/** Check project-level management permissions (settings, contributor, etc.) */ export async function checkProjectPermissions(queryClient, orgName, projectName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    return getCanManageProjConfig(resolved.org, resolved.user, {
        id: projectName
    });
}
/** Check team creation permission */ export async function checkCreateTeamPermission(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return false;
    const result = await getCanCreateProjectTeams(resolved.org, resolved.user);
    return result.can;
}
/** Check application-level management permissions */ export async function checkAppPermissions(queryClient, orgName, projectName, appName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    return getCanManageAppConfig(resolved.org, resolved.user, {
        id: appName,
        projectId: projectName
    });
}
/** Check application permissions AND devops feature flag (for release pipeline routes) */ export async function checkAppDevopsPermissions(queryClient, orgName, projectName, appName) {
    const [can, devopsEnabled] = await Promise.all([
        checkAppPermissions(queryClient, orgName, projectName, appName),
        ensureDevopsFeature(queryClient)
    ]);
    return {
        can,
        devopsEnabled
    };
}
/** Fetch all org-level permissions needed by the Layout navigation */ export async function getOrgRoutePermissions(queryClient, orgName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    const [org, canManageIntegrations, canManageSubscription, canViewSubscription] = await Promise.all([
        getCanManageOrgConfig(resolved.org, resolved.user),
        getCanManageIntegrations(resolved.org, resolved.user),
        getCanManageSubscription(resolved.org, resolved.user),
        getCanViewSubscription(resolved.org, resolved.user)
    ]);
    return {
        org,
        canManageIntegrations,
        canManageSubscription,
        canViewSubscription
    };
}
/** Fetch all project-level permissions needed by the Layout navigation */ export async function getProjectRoutePermissions(queryClient, orgName, projectName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    const project = await getCanManageProjConfig(resolved.org, resolved.user, {
        id: projectName
    });
    return {
        project
    };
}
/** Fetch all app-level permissions needed by the Layout navigation */ export async function getAppRoutePermissions(queryClient, orgName, projectName, appName) {
    const resolved = await resolveOrgAndUser(queryClient, orgName);
    if (!resolved) return null;
    const [app, isDevopsAllowed] = await Promise.all([
        getCanManageAppConfig(resolved.org, resolved.user, {
            id: appName,
            projectId: projectName
        }),
        ensureDevopsFeature(queryClient)
    ]);
    return {
        app,
        isDevopsAllowed
    };
}
